(() => {
  if (window.richSolarImageZipInitialized) return;
  window.richSolarImageZipInitialized = true;

  const DOWNLOAD_DELAY = 120;
  const FETCH_TIMEOUT = 30000;
  const SUCCESS_MESSAGE_DURATION = 2000;
  const FOLDER_PERMISSION_MESSAGE = "Allow folder access to save product images.";
  const SUCCESS_ICON_SVG = '<svg t="1781592261537" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4974" width="200" height="200"><path d="M512 0C230.4 0 0 230.4 0 512s230.4 512 512 512 512-230.4 512-512-230.4-512-512-512zM409.6 761.6L172.8 544l57.6-64L409.6 640l384-384 64 64-448 441.6z" fill="green" p-id="4975"></path></svg>';
  const messageTimers = new WeakMap();

  const wait = (duration) => new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

  const shouldShowDownloadLocationHint = () => {
    return window.matchMedia("(max-width: 750px)").matches;
  };

  const textEncoder = new TextEncoder();
  const crcTable = (() => {
    const table = new Uint32Array(256);

    for (let index = 0; index < 256; index += 1) {
      let value = index;

      for (let bit = 0; bit < 8; bit += 1) {
        value = (value & 1) ? (0xedb88320 ^ (value >>> 1)) : (value >>> 1);
      }

      table[index] = value >>> 0;
    }

    return table;
  })();

  const getCrc32 = (bytes) => {
    let crc = 0xffffffff;

    for (let index = 0; index < bytes.length; index += 1) {
      crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
    }

    return (crc ^ 0xffffffff) >>> 0;
  };

  const writeUint16 = (view, offset, value) => {
    view.setUint16(offset, value, true);
  };

  const writeUint32 = (view, offset, value) => {
    view.setUint32(offset, value >>> 0, true);
  };

  const getDosDateTime = (date = new Date()) => {
    const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
    const dosDate = ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();

    return { dosDate, dosTime };
  };

  const createZipBlob = (files) => {
    const localParts = [];
    const centralParts = [];
    let offset = 0;
    const { dosDate, dosTime } = getDosDateTime();

    files.forEach((file) => {
      const nameBytes = textEncoder.encode(file.path);
      const dataBytes = new Uint8Array(file.buffer);
      const crc = getCrc32(dataBytes);
      const localHeader = new ArrayBuffer(30 + nameBytes.length);
      const localView = new DataView(localHeader);

      writeUint32(localView, 0, 0x04034b50);
      writeUint16(localView, 4, 20);
      writeUint16(localView, 6, 0x0800);
      writeUint16(localView, 8, 0);
      writeUint16(localView, 10, dosTime);
      writeUint16(localView, 12, dosDate);
      writeUint32(localView, 14, crc);
      writeUint32(localView, 18, dataBytes.byteLength);
      writeUint32(localView, 22, dataBytes.byteLength);
      writeUint16(localView, 26, nameBytes.length);
      writeUint16(localView, 28, 0);
      new Uint8Array(localHeader, 30).set(nameBytes);

      localParts.push(localHeader, dataBytes);

      const centralHeader = new ArrayBuffer(46 + nameBytes.length);
      const centralView = new DataView(centralHeader);

      writeUint32(centralView, 0, 0x02014b50);
      writeUint16(centralView, 4, 20);
      writeUint16(centralView, 6, 20);
      writeUint16(centralView, 8, 0x0800);
      writeUint16(centralView, 10, 0);
      writeUint16(centralView, 12, dosTime);
      writeUint16(centralView, 14, dosDate);
      writeUint32(centralView, 16, crc);
      writeUint32(centralView, 20, dataBytes.byteLength);
      writeUint32(centralView, 24, dataBytes.byteLength);
      writeUint16(centralView, 28, nameBytes.length);
      writeUint16(centralView, 30, 0);
      writeUint16(centralView, 32, 0);
      writeUint16(centralView, 34, 0);
      writeUint16(centralView, 36, 0);
      writeUint32(centralView, 38, 0);
      writeUint32(centralView, 42, offset);
      new Uint8Array(centralHeader, 46).set(nameBytes);

      centralParts.push(centralHeader);
      offset += localHeader.byteLength + dataBytes.byteLength;
    });

    const centralSize = centralParts.reduce((sum, part) => sum + part.byteLength, 0);
    const endHeader = new ArrayBuffer(22);
    const endView = new DataView(endHeader);

    writeUint32(endView, 0, 0x06054b50);
    writeUint16(endView, 4, 0);
    writeUint16(endView, 6, 0);
    writeUint16(endView, 8, files.length);
    writeUint16(endView, 10, files.length);
    writeUint32(endView, 12, centralSize);
    writeUint32(endView, 16, offset);
    writeUint16(endView, 20, 0);

    return new Blob([...localParts, ...centralParts, endHeader], { type: "application/zip" });
  };

  const sanitizeFilePart = (value) => {
    return String(value || "product-image")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase() || "product-image";
  };

  const sanitizeFolderPart = (value) => {
    return String(value || "product-image")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || "product-image";
  };

  const getExtension = (url, contentType) => {
    const typeMap = {
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/png": ".png",
      "image/webp": ".webp",
      "image/gif": ".gif",
      "image/avif": ".avif"
    };
    const normalizedType = String(contentType || "").split(";")[0].trim().toLowerCase();

    if (typeMap[normalizedType]) return typeMap[normalizedType];

    try {
      const path = new URL(url, window.location.href).pathname;
      const extension = path.match(/\.(jpe?g|png|webp|gif|avif)$/i);

      if (extension) return extension[0].toLowerCase().replace(".jpeg", ".jpg");
    } catch (error) {
      return ".jpg";
    }

    return ".jpg";
  };

  const canSaveToFolder = () => {
    return window.isSecureContext && "showDirectoryPicker" in window;
  };

  const getImages = (button) => {
    try {
      const images = JSON.parse(button.dataset.images || "[]");

      return Array.isArray(images) ? images.filter((image) => image && image.url) : [];
    } catch (error) {
      console.error("Unable to read product images.", error);
      return [];
    }
  };

  const emitDownloadState = (button, state, detail = {}) => {
    button.dispatchEvent(new CustomEvent("rs:image-zip:state", {
      bubbles: true,
      detail: { state, ...detail }
    }));
  };

  const setButtonState = (button, label, disabled) => {
    const labelNode = button.querySelector("[data-rs-zip-label]");

    if (labelNode) labelNode.textContent = label;
    button.disabled = disabled;
    button.classList.toggle("is-loading", disabled);
  };

  const getDownloadMessage = (button) => {
    const wrapper = button.closest(".rs-image-download-ui");

    return wrapper ? wrapper.querySelector("[data-rs-download-message]") : null;
  };

  const hideDownloadMessage = (button) => {
    const message = getDownloadMessage(button);
    const timer = messageTimers.get(button);

    if (timer) {
      window.clearTimeout(timer);
      messageTimers.delete(button);
    }

    if (!message) return;

    message.classList.remove("is-visible");
    message.hidden = true;
  };

  const showDownloadMessage = (button, locationMessage) => {
    const message = getDownloadMessage(button);

    if (!message) return;

    hideDownloadMessage(button);
    message.innerHTML = shouldShowDownloadLocationHint() && locationMessage
      ? `${SUCCESS_ICON_SVG}<span>Download complete.<br>${locationMessage}</span>`
      : `${SUCCESS_ICON_SVG}<span>Download complete.</span>`;
    message.hidden = false;
    message.classList.add("is-visible");

    messageTimers.set(button, window.setTimeout(() => {
      message.classList.remove("is-visible");
      message.hidden = true;
      messageTimers.delete(button);
    }, SUCCESS_MESSAGE_DURATION));
  };

  const getDownloadName = (image, index, usedNames, contentType) => {
    const baseName = sanitizeFilePart(image.filename || `product-image-${index + 1}`);
    const extension = getExtension(image.url, contentType);
    let fileName = `${baseName}${extension}`;

    if (usedNames.has(fileName)) {
      fileName = `${baseName}-${index + 1}${extension}`;
    }

    usedNames.add(fileName);
    return fileName;
  };

  const getZipPath = (image, fileName) => {
    const folder = image.folder ? `${sanitizeFolderPart(image.folder)}/` : "";

    return `${folder}${fileName}`;
  };

  const getImageDirectory = async (rootDirectory, folderName, cache) => {
    const safeFolderName = sanitizeFolderPart(folderName);
    if (!safeFolderName) return rootDirectory;

    if (!cache.has(safeFolderName)) {
      cache.set(safeFolderName, await rootDirectory.getDirectoryHandle(safeFolderName, {
        create: true
      }));
    }

    return cache.get(safeFolderName);
  };

  const fetchImageBlob = async (image) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const response = await fetch(image.url, {
        credentials: "omit",
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Image request failed: ${response.status}`);
      }

      return {
        blob: await response.blob(),
        contentType: response.headers.get("content-type")
      };
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const saveImagesToFolder = async (button, images) => {
    const rootDirectory = await window.showDirectoryPicker({ mode: "readwrite" });

    // if (!window.confirm(FOLDER_PERMISSION_MESSAGE)) {
    //   return false;
    // }

    const productName = sanitizeFilePart(button.dataset.productTitle || button.dataset.productHandle);
    const imageDirectory = await rootDirectory.getDirectoryHandle(`${productName}-images`, {
      create: true
    });
    const folderCache = new Map();
    const usedNamesByFolder = new Map();

    for (let index = 0; index < images.length; index += 1) {
      const image = images[index];
      const targetDirectory = image.folder ? await getImageDirectory(imageDirectory, image.folder, folderCache) : imageDirectory;
      const folderKey = image.folder ? sanitizeFolderPart(image.folder) : "";
      const usedNames = usedNamesByFolder.get(folderKey) || new Set();
      const { blob, contentType } = await fetchImageBlob(image);
      const fileName = getDownloadName(image, index, usedNames, contentType);
      const fileHandle = await targetDirectory.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();

      usedNamesByFolder.set(folderKey, usedNames);
      await writable.write(blob);
      await writable.close();
    }

    return true;
  };

  const downloadImage = (image, fileName) => {
    const link = document.createElement("a");

    link.href = image.url;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener";
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const downloadBlob = (blob, fileName) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = fileName;
    link.rel = "noopener";
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadImagesAsZip = async (button, images) => {
    const usedNames = new Set();
    const files = [];

    for (let index = 0; index < images.length; index += 1) {
      const image = images[index];
      const { blob, contentType } = await fetchImageBlob(image);
      const fileName = getDownloadName(image, index, usedNames, contentType);

      files.push({
        path: getZipPath(image, fileName),
        buffer: await blob.arrayBuffer()
      });
    }

    const productName = sanitizeFilePart(button.dataset.productTitle || button.dataset.productHandle);
    downloadBlob(createZipBlob(files), `${productName}-images.zip`);
  };

  const downloadImages = async (images) => {
    const usedNames = new Set();

    for (let index = 0; index < images.length; index += 1) {
      const image = images[index];
      const fileName = getDownloadName(image, index, usedNames);

      downloadImage(image, fileName);

      if (index < images.length - 1) {
        await wait(DOWNLOAD_DELAY);
      }
    }
  };

  const handleImageDownloadClick = async (button) => {
    const images = getImages(button);
    const saveToFolder = canSaveToFolder();

    hideDownloadMessage(button);

    if (!images.length) {
      emitDownloadState(button, "idle");
      setButtonState(button, "No images", false);
      await wait(1500);
      setButtonState(button, "Download", false);
      return;
    }

    emitDownloadState(button, "loading");
    setButtonState(button, saveToFolder ? "Saving..." : "Downloading...", true);

    try {
      let isComplete = false;

      if (saveToFolder) {
        isComplete = await saveImagesToFolder(button, images);
        button.dataset.rsDownloadLocation = "Saved to the folder you selected.";
      } else if (images.length > 1 || window.theme?.device?.hasTouch) {
        await downloadImagesAsZip(button, images);
        isComplete = true;
        button.dataset.rsDownloadLocation = "Find the ZIP in your browser Downloads.";
      } else {
        await downloadImages(images);
        isComplete = true;
        button.dataset.rsDownloadLocation = "Find the file in your browser Downloads.";
      }

      setButtonState(button, "Download", false);

      if (isComplete) {
        const locationMessage = button.dataset.rsDownloadLocation || "Find the file in your browser Downloads.";
        emitDownloadState(button, "complete", {
          locationMessage: shouldShowDownloadLocationHint() ? locationMessage : ""
        });
        showDownloadMessage(button, locationMessage);
      }
    } catch (error) {
      if (error && (error.name === "AbortError" || error.name === "NotAllowedError")) {
        emitDownloadState(button, "idle");
        setButtonState(button, "Download", false);
        return;
      }

      console.error("Unable to download product images.", error);
      emitDownloadState(button, "error");
      setButtonState(button, "Try Again", false);
    }
  };

  window.richSolarDownloadImageZip = handleImageDownloadClick;

  document.addEventListener("click", (event) => {
    const button = event.target.closest(".rs-image-zip-btn");

    if (!button) return;

    event.preventDefault();
    handleImageDownloadClick(button);
  });
})();
